import React, { useState } from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import type { IChartTooltipTheme } from '../tooltipTheme';
import { visuallyHidden } from '../visuallyHidden';

/* `Small line chart widget` 589-29285: 142 x 56, a 3px `Line` (589-29286), a 14px dot
   (589-29287) with a 5px 16%-opacity halo, and a 1px 4,4-dashed drop-line from the dot to the
   floor (589-29288). The dot and its drop-line are a HOVER state, not a permanent mark. */
const SPARK_WIDTH = 142;
const SPARK_HEIGHT = 56;
const SPARK_STROKE = 3;
const SPARK_DOT_RADIUS = 7;
// The dot's own 5px border at 16%, which is what separates the marker from the stroke behind it.
const SPARK_HALO_WIDTH = 5;
const SPARK_HALO_OPACITY = 0.16;

const CHART_PATH = `${TCustomerInsights}.Chart`;

/* The PANEL plot box. The tile's own 142x56 is 2.54:1, and a fluid figure scales uniformly — so
   filling a 542px panel with it produced a chart 214px TALL, four times the tile's height and far
   more vertical weight than a trend beside four reading rows should carry. That was the "too big".

   10:1 instead, which puts the same panel between 27px (a 268px column at 1440) and 54px (a 542px
   column at 1280) — proportionate at both, and never taller than the tile plot it shares a widget
   with. `preserveAspectRatio` stays at its uniform default rather than `none`, because stretching
   one axis turns the hover marker from a circle into an ellipse.

   The RATIO IS A CHOICE, not a measurement: `2558:3932` and `2570:4672` draw these panel trends
   with no height this side has read. UNVERIFIED, and one constant to change if the designer gives
   a number. PLA-8815. */
const PANEL_PLOT_WIDTH = 560;
const PANEL_PLOT_HEIGHT = 56;

export interface ISparkPoint {
  index: number;
  reading: number;
  x: number;
  y: number;
}

/**
 * Plot geometry as a pure function of the readings handed in, so the widget survives the seam
 * turning live: the count sets the x pitch and the readings' own min and max set the y range. No
 * point count, range or sign is assumed anywhere. Exported for its own tests — this is the part
 * that has to hold for data nobody has seen yet, and asserting it through a rendered fixture would
 * only ever exercise the nine readings the fixture ships.
 *
 * A non-finite reading keeps its x slot and yields `null` rather than being filtered out — dropping
 * it would slide every later point left and redraw the trend as if the gap had never existed.
 */
/** Clear space around the line: one figure for every edge, or per edge. */
export type SparkInset = number | { x: number; top: number; bottom: number };

export const plotSparkline = (
  points: number[],
  plotWidth: number = SPARK_WIDTH,
  plotHeight: number = SPARK_HEIGHT,
  inset: SparkInset = SPARK_DOT_RADIUS
): (ISparkPoint | null)[] => {
  const readings = points.map(point => (Number.isFinite(point) ? point : null));
  const measured = readings.filter((reading): reading is number => reading !== null);

  if (measured.length === 0) {
    return [];
  }

  const peak = Math.max(...measured);
  const floor = Math.min(...measured);
  const span = peak - floor;
  // Half a stroke of headroom under the plot and a dot's radius over it, so neither the 3px line
  // nor a 14px marker is clipped at an edge.
  const edges = typeof inset === 'number' ? { x: inset, top: inset, bottom: inset } : inset;
  const top = edges.top;
  const bottom = plotHeight - edges.bottom - SPARK_STROKE / 2;
  const track = plotWidth - edges.x * 2;
  // One reading would divide by zero; it also draws no segment, which the caller rejects first.
  const pitch = Math.max(points.length - 1, 1);

  return readings.map((reading, index) => {
    if (reading === null) {
      return null;
    }

    return {
      index,
      reading,
      x: (index / pitch) * track + edges.x,
      // A flat series has no range to spread over, so it draws level through the middle rather
      // than pinned to whichever edge a zero denominator happens to produce.
      y: span === 0 ? (top + bottom) / 2 : top + (1 - (reading - floor) / span) * (bottom - top)
    };
  });
};

// A stroke needs two ADJACENT measured readings: one reading is a level, and two either side of a
// gap paint nothing. Same rule `TrendChart` applies, and the same reason — an empty plot box inside
// a sized tile reads as a broken card.
export const sparkSegments = (plotted: (ISparkPoint | null)[]): ISparkPoint[][] =>
  plotted
    .reduce(
      (runs: ISparkPoint[][], point) => {
        if (point === null) {
          return [...runs, []];
        }

        const current = runs[runs.length - 1];

        current.push(point);

        return runs;
      },
      [[]]
    )
    .filter(run => run.length > 1);

/* Catmull-Rom through every point of a run, emitted as cubic Beziers: the smooth spline the
   Overwatch tiles draw (PLA-9674) instead of the polyline. */
export const smoothSparkPath = (run: ISparkPoint[]): string => {
  const curves = run.slice(1).map((point, i) => {
    const before = run[i - 1] || run[i];
    const from = run[i];
    const after = run[i + 2] || point;

    return `C${from.x + (point.x - before.x) / 6},${from.y + (point.y - before.y) / 6} ${
      point.x - (after.x - from.x) / 6
    },${point.y - (after.y - from.y) / 6} ${point.x},${point.y}`;
  });

  return [`M${run[0].x},${run[0].y}`, ...curves].join(' ');
};

const linearSparkPath = (run: ISparkPoint[]): string =>
  run.map((point, i) => `${i === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ');

/* `inline-flex` is shrink-to-fit, so a fluid figure inside it fills 142px of box rather than 142px
   of column — the SVG's `width: 100%` resolves against a wrapper that has already collapsed to the
   figure's own attribute width. The fluid variant therefore has to widen the WRAPPER too; setting it
   on the figure alone measured 142 inside a 307px panel. PLA-8815. */
const SparkBox = styled.span<{ $fluid: boolean }>`
  position: relative;
  display: ${({ $fluid }) => ($fluid ? 'flex' : 'inline-flex')};
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};
`;

/* Focusable, because the reading it reveals is available on hover and a keyboard reader has no
   hover. `role="img"` with the series' own name, and the active reading announced from the live
   region beside it rather than from inside the image, whose contents assistive tech ignores. */
/* The `$fluid` rule below scales the whole 142x56 viewBox to its column rather than replotting at a
   new size, so the stroke, the dot and its halo keep the proportions measured off 589-29285. Uniform
   scaling, so the marker stays a circle: preserveAspectRatio "none" would draw it as an ellipse at
   any column width but 142.

   Kept OUT of the styled literal: a backtick inside a CSS comment closes the template and takes the
   whole tree's typecheck down with it (design/tools/README-CAPTURES.md). PLA-8815. */
const SparkFigure = styled.svg<{ $fluid: boolean }>`
  display: block;
  border-radius: ${tokens.radius.xs};

  ${({ $fluid }) => ($fluid ? 'width: 100%; height: auto;' : '')}

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

/* The visible reading: a figure in a small box above the marker, or below it where the marker sits
   too near the top. Its width is estimated from the character count, since an SVG text box cannot
   be measured before it is drawn. */
const SparkValue = styled.text`
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
`;

const READING_GAP = 6;
const READING_HEIGHT = 22;
const READING_CHAR_WIDTH = 6.6;
const READING_PADDING = 16;

const DEFAULT_READING_THEME: IChartTooltipTheme = {
  background: tokens.color['surface-card'],
  border: tokens.color['border-subtle'],
  text: tokens.color['text-primary'],
  label: tokens.color['text-muted']
};

const SparkReading = styled.span`
  ${visuallyHidden}
`;

export interface ITileSparklineProps {
  points: number[];
  color: string;
  /** Names the plot, and the reading the marker reveals. */
  label: string;
  formatReading: (reading: number) => string;
  /**
   * Root test id; the marker and the live readout take it as their own prefix. Defaulted rather
   * than fixed because Risk & Protection's suite already addresses this widget as
   * `risk-tile-sparkline`, and a shared component may not rename the ids of a band it moved out of.
   */
  testId?: string;
  /**
   * Fills its column instead of drawing at the tile's own 142x56. The two panel trends the industry
   * bands draw (`ARPU MOVEMENT · DAILY TREND`, `WALLET ACTIVE USER RATE · WEEKLY TREND`) are the
   * same widget at panel scale, not a second chart.
   */
  fluid?: boolean;
  /** `smooth` draws the run as a spline rather than the tile's straight polyline. */
  curve?: 'linear' | 'smooth';
  /** Plot box in px, overriding the tile (142x56) and panel sizes. */
  plotSize?: { width: number; height: number };
  /** Marker radius in px, excluding its 5px halo. */
  dotRadius?: number;
  strokeWidth?: number;
  /**
   * Space kept clear between the line and the plot edge; defaults to the marker radius. Per edge
   * where a screen draws the line hugging the top of its band with fill below it (PLA-9674 A1).
   */
  inset?: SparkInset;
  /**
   * Prints the hovered or focused reading beside the marker. Off by default: the Intelligence tiles
   * reveal the marker only and announce the reading to assistive tech.
   */
  showReading?: boolean;
  /** The reading box in a screen's own palette. */
  tooltipTheme?: IChartTooltipTheme;
  /** Fills under the line with a gradient from `from` at 30% to the line colour at 0%. */
  area?: { from: string };
  className?: string;
}

/**
 * The tile trend of 589-29280 and of Risk & Protection's tile 3, shared since PLA-8815 put the same
 * marked line on the four tiles of Telco's Churn & Service Experience card (`2558:3744`) — the
 * chart the FR names by pointing at the one already drawn on High-risk Customers.
 *
 * NOT `TrendChart`, and the reason is the hover rather than the drawing: this repo is on recharts
 * 2.5.0, whose `Tooltip` is pointer-only — `accessibilityLayer` arrived in 2.10 — so its hover
 * cannot be reached from a keyboard, which is the one thing a reveal-on-hover reading must be.
 * Its reveal is also a different shape from this one: a floating bordered tooltip box (wider than
 * this 142px plot, inside an 84px tile) over a solid full-height cursor line, where the file draws
 * a haloed dot and a dashed drop-line and no box at all. Its 2.5px stroke is a deliberate
 * normalisation shared with `StatBox`, so widening it here would restyle every trend on the page.
 *
 * The marker follows the HOVERED point rather than staying pinned to the peak. The captures cannot
 * separate the two — a still of a hover looks exactly like a permanent mark, and both tiles happen
 * to be captured at their own maximum — so this takes the behaviour a reader expects of a chart
 * that responds to the pointer, and the one that still means something when the data changes.
 * Focus enters at the peak, which is the state the file is drawn in.
 *
 * Tile 3's own widget node was NOT resolved: three ring walks (589-29289 -> 3 children,
 * 589-29260 -> 17, 589-29259 -> 3) all report the tile as label + value + `High` only, yet two
 * independent 93% and 100% canvas captures draw the green marked line inside its box. It is built
 * from what the captures show, and its id is unresolved rather than absent.
 */
const TileSparkline: React.FC<ITileSparklineProps> = ({
  points,
  color,
  label,
  formatReading,
  testId = 'tile-sparkline',
  fluid = false,
  curve = 'linear',
  plotSize,
  dotRadius = SPARK_DOT_RADIUS,
  strokeWidth = SPARK_STROKE,
  inset,
  area,
  showReading = false,
  tooltipTheme = DEFAULT_READING_THEME,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const [active, setActive] = useState<number | null>(null);
  // useId wraps its ids in colons, which a url(#...) paint reference does not reliably resolve.
  const gradientId = `tile-sparkline-area-${React.useId().replace(/:/g, '')}`;
  const plotWidth = plotSize?.width ?? (fluid ? PANEL_PLOT_WIDTH : SPARK_WIDTH);
  const plotHeight = plotSize?.height ?? (fluid ? PANEL_PLOT_HEIGHT : SPARK_HEIGHT);
  const plotted = plotSparkline(points, plotWidth, plotHeight, inset ?? dotRadius);
  const segments = sparkSegments(plotted);
  const measured = plotted.filter((point): point is ISparkPoint => point !== null);

  if (segments.length === 0) {
    return null;
  }

  const peak = measured.reduce(
    (highest, point) => (point.reading > highest.reading ? point : highest),
    measured[0]
  );
  const marker = active === null ? null : plotted[active] ?? null;
  // Nearest measured point to the pointer, in the plot's own coordinates, so the reading tracks
  // the cursor at any rendered width rather than at the 142 the viewBox declares.
  const nearestTo = (clientX: number, box: DOMRect): ISparkPoint => {
    const ratio = box.width > 0 ? (clientX - box.left) / box.width : 0;
    const at = ratio * plotWidth;

    return measured.reduce(
      (closest, point) => (Math.abs(point.x - at) < Math.abs(closest.x - at) ? point : closest),
      measured[0]
    );
  };
  // Arrow keys step through the MEASURED readings, so a gap is skipped rather than landing the
  // marker on a point that was never recorded.
  const step = (delta: number) => {
    const from = measured.findIndex(point => point.index === active);
    const next = from < 0 ? measured.indexOf(peak) : from + delta;

    return measured[Math.min(Math.max(next, 0), measured.length - 1)].index;
  };
  const onKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    const moves: Record<string, () => number> = {
      ArrowRight: () => step(1),
      ArrowLeft: () => step(-1),
      Home: () => measured[0].index,
      End: () => measured[measured.length - 1].index
    };

    if (moves[event.key]) {
      event.preventDefault();
      setActive(moves[event.key]());
    }
  };

  return (
    <SparkBox className={className} $fluid={fluid}>
      <SparkFigure
        $fluid={fluid}
        style={showReading ? { overflow: 'visible' } : undefined}
        width={plotWidth}
        height={plotHeight}
        viewBox={`0 0 ${plotWidth} ${plotHeight}`}
        role="img"
        tabIndex={0}
        aria-label={t(`${CHART_PATH}.TrendSeries`, { series: label })}
        data-testid={testId}
        data-points={measured.length}
        onMouseMove={event =>
          setActive(nearestTo(event.clientX, event.currentTarget.getBoundingClientRect()).index)
        }
        onMouseLeave={() => setActive(null)}
        onFocus={() => setActive(current => (current === null ? peak.index : current))}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
      >
        {area ? (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={area.from} stopOpacity={0.3} />
              <stop offset="1" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
        ) : null}
        {area
          ? segments.map(run => (
              <path
                key={`area-${run[0].index}`}
                d={`${curve === 'smooth' ? smoothSparkPath(run) : linearSparkPath(run)} L${
                  run[run.length - 1].x
                },${plotHeight} L${run[0].x},${plotHeight} Z`}
                fill={`url(#${gradientId})`}
                data-testid={`${testId}-area`}
              />
            ))
          : null}
        {segments.map(run =>
          curve === 'smooth' ? (
            <path
              key={run[0].index}
              d={smoothSparkPath(run)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <polyline
              key={run[0].index}
              points={run.map(point => `${point.x},${point.y}`).join(' ')}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )
        )}

        {marker ? (
          <g data-testid={`${testId}-marker`}>
            <line
              x1={marker.x}
              y1={marker.y}
              x2={marker.x}
              y2={plotHeight}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <circle
              cx={marker.x}
              cy={marker.y}
              r={dotRadius}
              fill={color}
              stroke={color}
              strokeWidth={SPARK_HALO_WIDTH}
              strokeOpacity={SPARK_HALO_OPACITY}
            />
          </g>
        ) : null}

        {showReading && marker
          ? (() => {
              const text = formatReading(marker.reading);
              const width = text.length * READING_CHAR_WIDTH + READING_PADDING;
              const above = marker.y - dotRadius - READING_GAP - READING_HEIGHT;
              const top = above < 0 ? marker.y + dotRadius + READING_GAP : above;
              const left = Math.min(Math.max(marker.x - width / 2, 0), plotWidth - width);

              return (
                <g data-testid={`${testId}-value-box`}>
                  <rect
                    x={left}
                    y={top}
                    width={width}
                    height={READING_HEIGHT}
                    rx={6}
                    fill={tooltipTheme.background}
                    stroke={tooltipTheme.border}
                  />
                  <SparkValue
                    x={left + width / 2}
                    y={top + READING_HEIGHT / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={tooltipTheme.text}
                    data-testid={`${testId}-value`}
                  >
                    {text}
                  </SparkValue>
                </g>
              );
            })()
          : null}
      </SparkFigure>

      <SparkReading aria-live="polite" data-testid={`${testId}-reading`}>
        {marker
          ? t(`${CHART_PATH}.PointReading`, { label, value: formatReading(marker.reading) })
          : ''}
      </SparkReading>
    </SparkBox>
  );
};

export default TileSparkline;
